import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  useTheme,
  styled,
  alpha,
  Collapse,
  Grid,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
} from "@mui/material";
import {
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Person,
  LocationOn,
  LocalShipping,
  Payment,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh,
  ShoppingCart,
  Close as CloseIcon,
} from "@mui/icons-material";
import { getAdvancePaymentEntries } from "Network/paymentStatusService";

const formatDateToIST = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    const dateObj = new Date(dateString);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString('en-GB', { month: 'short', timeZone: 'Asia/Kolkata' });
    const year = dateObj.getFullYear();
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${day} ${month} ${year}, ${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return "—";
  }
};

const formatRequestDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const dateObj = new Date(dateString);
    if (isNaN(dateObj.getTime())) return "—";
    return dateObj.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

// ✅ FIXED — removed backgroundColor from .header, now comes from global theme
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    // ✅ backgroundColor removed — handled globally by components.js
    color: theme.palette.text.primary,
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& > .MuiTableCell-root": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  "& > .MuiTableCell-root": {
    borderBottom: "none",
  },
}));

const DetailCard = styled(Card)(({ theme }) => ({
  margin: "8px 0",
  padding: "20px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: "all 0.3s ease-in-out",
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusConfig = (status) => {
    const statusStr = status != null ? String(status).toLowerCase() : "";
    const configs = {
      pending: {
        bg: "#FFB74D",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      confirmed: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      processing: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      shipped: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <LocalShipping sx={{ fontSize: "16px !important" }} />,
      },
      dispatch: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <LocalShipping sx={{ fontSize: "16px !important" }} />,
      },
      delivered: {
        bg: "#66BB6A",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      cancelled: {
        bg: "#EF5350",
        text: "#FFFFFF",
        icon: <Cancel sx={{ fontSize: "16px !important" }} />,
      },
      returned: {
        bg: "#BA68C8",
        text: "#FFFFFF",
        icon: <Refresh sx={{ fontSize: "16px !important" }} />,
      },
      orders: {
        bg: "#FFB74D",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
    };
    return (
      configs[statusStr] || {
        bg: theme.palette.grey[400],
        text: theme.palette.common.white,
        icon: null,
      }
    );
  };

  const config = getStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: "capitalize",
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    padding: "4px 10px",
    minWidth: "auto",
    "& .MuiChip-icon": {
      color: config.text,
      marginLeft: "4px",
      marginRight: "4px",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "0.875rem",
      color: config.text,
      fontWeight: 600,
    },
  };
});

const PaymentStatusChip = styled(Chip)(({ theme, status }) => {
  const getPaymentStatusConfig = (status) => {
    const statusStr = status != null ? String(status).toLowerCase() : "";
    const configs = {
      pending: {
        bg: "#fcc874",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      outstanding: {
        bg: "#888cff",
        text: "#FFFFFF",
        icon: <Schedule sx={{ fontSize: "16px !important" }} />,
      },
      paid: {
        bg: "#22d28a",
        text: "#FFFFFF",
        icon: <CheckCircle sx={{ fontSize: "16px !important" }} />,
      },
      failed: {
        bg: "#f4346e",
        text: "#FFFFFF",
        icon: <Cancel sx={{ fontSize: "16px !important" }} />,
      },
      refunded: {
        bg: "#888cff",
        text: "#FFFFFF",
        icon: <Refresh sx={{ fontSize: "16px !important" }} />,
      },
    };
    return (
      configs[statusStr] || {
        bg: theme.palette.grey[400],
        text: "#FFFFFF",
        icon: null,
      }
    );
  };

  const config = getPaymentStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: "capitalize",
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    padding: "4px 10px",
    minWidth: "auto",
    "& .MuiChip-icon": {
      color: config.text,
      marginLeft: "4px",
      marginRight: "4px",
    },
    "& .MuiChip-label": {
      padding: "0 4px",
      fontSize: "0.875rem",
      color: config.text,
      fontWeight: 600,
    },
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 600,
  overflowX: "hidden",
  borderRadius: 2,
  boxShadow: "none",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.black, 0.05),
    borderRadius: '10px',
    margin: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.common.black, 0.2),
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: alpha(theme.palette.common.black, 0.3),
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.common.black, 0.2)} ${alpha(theme.palette.common.black, 0.05)}`,
  ...(theme.palette.mode === 'dark' && {
    '&::-webkit-scrollbar-track': {
      background: alpha(theme.palette.common.white, 0.05),
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.common.white, 0.2),
      '&:hover': {
        background: alpha(theme.palette.common.white, 0.3),
      },
    },
    scrollbarColor: `${alpha(theme.palette.common.white, 0.2)} ${alpha(theme.palette.common.white, 0.05)}`,
  }),
}));

const formatCurrency = (amount) => {
  if (amount == null || amount === "") return "—";
  return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
};

const formatPaymentCurrency = (amount) => {
  const value = parseFloat(amount);
  return `₹${(Number.isNaN(value) ? 0 : value).toLocaleString("en-IN")}`;
};

const formatOrderDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const ADVANCE_PAYMENT_FIELD_LABELS = {
  transaction_id: "Transaction ID",
  transaction_reference_no: "Transaction Reference No.",
  upi_id_used: "UPI ID",
  payment_screenshot: "Payment Screenshot",
  sender_bank_name: "Sender Bank Name",
  payment_confirmation_file: "Payment Confirmation File",
  cheque_number: "Cheque Number",
  bank_name: "Bank Name",
  cheque_date: "Cheque Date",
  cheque_amount: "Cheque Amount",
  cheque_image: "Cheque Image",
  cash_receipt_number: "Cash Receipt No.",
  receiver_name: "Receiver Name (Staff)",
  bank_Name: "Bank Name",
};

const getAdvancePaymentFieldRows = (entry = {}) => {
  const fields = entry?.fields;
  if (!fields || typeof fields !== "object") return [];
  const skipKeys = new Set(["id", "created_at", "updated_at", "is_deleted"]);
  return Object.entries(fields)
    .filter(([key, val]) => !skipKeys.has(key) && val != null && String(val).trim() !== "")
    .map(([key, val]) => ({
      key,
      label:
        ADVANCE_PAYMENT_FIELD_LABELS[key] ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value: String(val),
    }));
};

function AdvancePaymentEntryCard({ entry, index, theme }) {
  const fieldRows = getAdvancePaymentFieldRows(entry);
  const recordedBy =
    entry.user?.full_name ||
    [entry.user?.first_name, entry.user?.last_name].filter(Boolean).join(" ") ||
    "—";
  const [previewUrl, setPreviewUrl] = useState(null);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: index > 0 ? 0 : 0,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.04),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Entry #{index + 1}
          {entry.is_full_payment ? " · Full payment" : " · Partial payment"}
        </Typography>
        <Chip
          size="small"
          label={entry.method?.name || "—"}
          color="warning"
          variant="outlined"
        />
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>{entry?.remaining_amount === 0 || entry?.remaining_amount === 0.0 ? "Remaining Paid Amount" : "Advance Paid Amount"}:</strong> {formatPaymentCurrency(entry.advance_amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Remaining Amount:</strong> {formatPaymentCurrency(entry.remaining_amount)}
          </Typography>
        </Grid>
        {entry.transaction_id ? (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Transaction ID:</strong> {entry.transaction_id}
            </Typography>
          </Grid>
        ) : null}
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Recorded By:</strong> {recordedBy}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Recorded At:</strong> {formatOrderDateTime(entry.created_at)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Updated At:</strong> {formatOrderDateTime(entry.updated_at)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Note:</strong>{" "}
            {entry.comment != null && String(entry.comment).trim() !== "" ? entry.comment : "N/A"}
          </Typography>
        </Grid>
      </Grid>

      {fieldRows.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Payment method details
          </Typography>
          <Grid container spacing={1}>
            {fieldRows.map((row) => (
              <Grid item xs={12} sm={6} key={`${entry.id}-${row.key}`}>
                {row.key === "payment_screenshot" ||
                row.key === "payment_confirmation_file" ||
                row.key === "cheque_image" ? (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                  >
                    <strong>{row.label}:</strong>
                    {row.value ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPreviewUrl(row.value)}
                        sx={{
                          textTransform: "none",
                          border: "1px solid #1e293b",
                          borderRadius: "8px",
                          px: "18px",
                          py: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1e293b",
                          bgcolor: "#fff",
                          "&:hover": {
                            border: "1px solid #1e293b",
                            bgcolor: "#f1f5f9",
                          },
                        }}
                      >
                        View
                      </Button>
                    ) : null}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <strong>{row.label}:</strong> {row.value}
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {entry.image && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <strong>Attachment:</strong>{" "}
          <Box
            component="a"
            href={entry.image}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ wordBreak: "break-all" }}
          >
            View file
          </Box>
        </Typography>
      )}

      <Dialog
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            p: 2,
            maxWidth: "90vw",
            maxHeight: "90vh",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.6)",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setPreviewUrl(null)}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={previewUrl || ""}
            alt="Payment Screenshot"
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </Box>
      </Dialog>
    </Paper>
  );
}

function AdvancePaymentDetailsSection({ order, theme }) {
  const entries = getAdvancePaymentEntries(order);
  if (entries.length === 0) return null;

  return (
    <Grid item xs={12}>
      <DetailCard
        elevation={2}
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          borderRadius: 3,
          p: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
          <Typography variant="h6" fontWeight={600}>
            Advance Payment Details ({entries.length})
          </Typography>
        </Box>
        {entries.map((entry, index) => (
          <Box key={entry.id ?? `advance-${index}`} sx={{ mb: index < entries.length - 1 ? 2 : 0 }}>
            <AdvancePaymentEntryCard entry={entry} index={index} theme={theme} />
          </Box>
        ))}
      </DetailCard>
    </Grid>
  );
}

const getStatusDisplay = (order) => {
  const name = order.status?.name ?? order.order_status_display ?? "";
  let category = order.status?.category;
  if (!category && typeof order.status === "string") {
    category = order.status;
  }
  const categoryStr = category != null ? String(category) : "";
  return { name: String(name || ""), category: categoryStr };
};

const getPaymentStatusDisplay = (order) => {
  const name = order.payment_status?.name ?? order.payment_status_display ?? "";
  let category = order.payment_status?.category;
  if (!category && typeof order.payment_status === "string") {
    category = order.payment_status;
  }
  const categoryStr = category != null ? String(category) : "";
  return { name: String(name || ""), category: categoryStr };
};

const getRequestAction = (order, field = "cancel_request_action") => {
  const raw = order[field] ?? order.request_action ?? order.request_status ?? order.status;
  if (raw == null || raw === "") return null;
  const s = String(raw).toLowerCase().trim();
  if (s === "approved" || s === "accept") return "approved";
  if (s === "rejected" || s === "reject") return "rejected";
  return null;
};

const getStatusIcon = (status) => {
  let category;
  if (typeof status === "object" && status !== null && "category" in status) {
    category = status.category;
  } else if (typeof status === "object" && status !== null && "name" in status) {
    category = status.name;
  } else {
    category = status;
  }
  const categoryStr = category != null ? String(category).toLowerCase() : "";
  const icons = {
    pending: <Schedule />,
    confirmed: <CheckCircle />,
    processing: <Schedule />,
    shipped: <LocalShipping />,
    dispatch: <LocalShipping />,
    delivered: <CheckCircle />,
    cancelled: <Cancel />,
    returned: <Refresh />,
  };
  return icons[categoryStr] || <Schedule />;
};

const ReturnCancelRequestTable = ({
  data = [],
  searchQuery = "",
  isLoading = false,
  selectedProducts = [],
  onProductSelect,
  onSelectAll,
  isAllSelected = false,
  showCancelRequestActions = false,
  onApproveCancelRequest,
  onRejectCancelRequest,
  updatingCancelRequestOrderId = null,
  showReturnRequestActions = false,
  onApproveReturnRequest,
  onRejectReturnRequest,
  updatingReturnRequestOrderId = null,
  onCheckPaymentStatus,
  checkingPaymentOrderId = null,
  currentPage = 1,
  pageSize = 10,
  shouldEnableButton,
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const filteredData = data;

  const toggleRow = (orderId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAllToggle = (e) => {
    if (onSelectAll) onSelectAll(e.target.checked);
  };

  const isProductSelected = (id) => selectedProducts.includes(id);

  const OrderDetailsRow = ({ order }) => {
    const statusDisplay = getStatusDisplay(order);
    const paymentDisplay = getPaymentStatusDisplay(order);
    const statusKey = (order.status?.category || order.status?.name || statusDisplay.category || statusDisplay.name || "").toString().toLowerCase();
    const orderItems = Array.isArray(order.items) ? order.items : [];

    return (
      <ExpandableRow>
        <StyledTableCell colSpan={8} sx={{ paddingY: 0, paddingX: 0 }}>
          <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
            <Box sx={{ width: "100%", padding: 2 }}>
              <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Person sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={600}>Customer Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {order.user?.full_name || order.user_name || order.full_name || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong> {order.order_id || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {order.user?.email || order.user_email || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order Date:</strong> {order.order_date_formatted || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Mobile:</strong> {order.user_phone || order.user?.mobile_number || order.mobile_number || "N/A"}
                    </Typography>

                    {(order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name) && (
                      <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                          Order on Behalf of : {order.farmer_name || getAdvancePaymentEntries(order)[0]?.order?.farmer_name}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Delivery Address</Typography>
                    </Box>
                    <Tooltip title={order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : "Address not available")} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}>
                        {order.delivery_address || (order.address_details ? `${order.address_details.full_address}, ${order.address_details.village}, ${order.address_details.tehsil}, ${order.address_details.pincode}` : "Address not available")}
                      </Typography>
                    </Tooltip>

                    {order.notes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                          {order.notes}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
                      <LocationOn sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="subtitle2" fontWeight={600}>Billing Address</Typography>
                    </Box>
                    <Tooltip title={order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : "Billing address not available")} arrow placement="top">
                      <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}>
                        {order.billing_address || (order.bill_address_details ? `${order.bill_address_details.full_address}, ${order.bill_address_details.village}, ${order.bill_address_details.tehsil}, ${order.bill_address_details.pincode}` : "Billing address not available")}
                      </Typography>
                    </Tooltip>
                  </DetailCard>
                </Grid>

                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard elevation={2} sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2.5, backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
                      <LocalShipping sx={{ mr: 1.25, fontSize: "1.25rem", color: theme.palette.success.main }} />
                      <Typography variant="h6" fontWeight={600}>Order Status</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          <strong>Order Status:</strong>{" "}
                          <StatusChip
                            label={order.status?.name || order.order_status_display || statusDisplay.name || "N/A"}
                            status={statusKey}
                            size="small"
                            icon={getStatusIcon(order.status)}
                          />
                        </Typography>
                      </Box>
                    </Box>

                    {order.expected_delivery_date && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2"><strong>Expected Delivery Date (Farmer):</strong></Typography>
                        <Typography variant="body2" color="primary" fontWeight={600} sx={{ mt: 0.5 }}>
                          {new Date(order.expected_delivery_date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                        </Typography>
                      </Box>
                    )}

                    {order.delivery_date && ["delivered", "returned"].includes(statusKey) && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Actual Delivery Date:</strong>{" "}
                          <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                            {(() => {
                              try {
                                const date = new Date(order.delivery_date);
                                return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
                              } catch (e) {
                                return order.delivery_date_formatted || order.delivery_date;
                              }
                            })()}
                          </Typography>
                        </Typography>
                      </Box>
                    )}

                    {statusKey === "returned" && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Returned At:</strong>{" "}
                          {(() => {
                            const dt = order.returned_at ?? order.status?.returned_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {statusKey === "cancelled" && (
                      <Box sx={{ mt: 2.5, mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Cancelled At:</strong>{" "}
                          {(() => {
                            const dt = order.cancelled_at ?? order.status?.cancelled_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {order.reason && (
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Reason:</strong> {order.reason}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Payment sx={{ mr: 1.25, fontSize: "1.25rem", color: theme.palette.info.main }} />
                      <Typography variant="subtitle1" fontWeight={600}>Payment Information</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Payment Status:</strong>
                      <PaymentStatusChip
                        label={order.payment_status?.name || order.payment_status_display || paymentDisplay.name || "Pending"}
                        status={(order.payment_status?.name || order.payment_status || paymentDisplay.category || "pending")?.toLowerCase()}
                        size="small"
                        sx={{ ml: 1.25 }}
                      />
                    </Typography>

                    {order?.latest_refund && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> ₹{order?.latest_refund?.amount ?? "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong> {order?.latest_refund?.refunded_at ? new Date(order.latest_refund.refunded_at).toLocaleString() : "N/A"}
                        </Typography>
                      </>
                    )}

                    {(order.payment_status?.name || order.payment_status || "").toString().toLowerCase() === "refunded" && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded Amount:</strong> {order.refunded_amount ?? order.payment_status?.refunded_amount ?? "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Refunded At:</strong>{" "}
                          {(() => {
                            const dt = order.refunded_at ?? order.payment_status?.refunded_at;
                            if (!dt) return "N/A";
                            try {
                              const date = new Date(dt);
                              return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      </>
                    )}

                    {order?.latest_refund && !order?.latest_refund?.is_success && onCheckPaymentStatus && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCheckPaymentStatus(order);
                        }}
                        disabled={checkingPaymentOrderId === (order?.order_id ?? order?.id)}
                        sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                      >
                        {checkingPaymentOrderId === (order?.order_id ?? order?.id) ? <CircularProgress size={18} sx={{ mr: 0.5 }} /> : null}
                        Check refund status
                      </Button>
                    )}
                  </DetailCard>
                </Grid>

                <AdvancePaymentDetailsSection order={order} theme={theme} />

                <Grid item xs={12}>
                  <DetailCard elevation={2} sx={{ backgroundColor: "background.paper", border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h5" fontWeight={600}>Order Items ({order.total_items ?? orderItems.length})</Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: "80px" }}>Sr. No.</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderItems.map((item, index) => (
                            <TableRow key={index} sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }} title={item.product_name}>
                                  {item.product_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "normal", wordBreak: "break-word" }} title={item.category_name || order.category_name || "N/A"}>
                                  {item.category_name || order.category_name || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell><Typography variant="body2" color="text.secondary">{item.product_sku}</Typography></TableCell>
                              <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                              <TableCell align="right">{parseFloat(item.quantity)}</TableCell>
                              <TableCell align="right">{parseFloat(item.gst_rate)}%</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                  {formatCurrency(item.item_total_with_gst)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Total Items:</strong> {order.total_items ?? orderItems.length}</Typography>
                        <Typography variant="body2"><strong>Total Quantity:</strong> {parseFloat(order.total_quantity ?? orderItems.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0))}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Subtotal:</strong> {formatCurrency(order.subtotal)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>GST Amount:</strong> {formatCurrency(order.gst_amount)}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Delivery Charge:</strong> {formatCurrency(order.delivery_charge)}</Typography>
                          {parseFloat(order.discount_amount) > 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}><strong>Discount:</strong> -{formatCurrency(order.discount_amount)}</Typography>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" color="primary.main" fontWeight={700}>Total: {formatCurrency(order.total_amount)}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </DetailCard>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </StyledTableCell>
      </ExpandableRow>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280, p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            {/* ✅ All backgroundColor removed — now comes from global theme grey[200] */}
            <StyledTableCell className="header" sx={{ width: 50 }}></StyledTableCell>
            <StyledTableCell className="header" sx={{ width: 80 }}>Sr. No.</StyledTableCell>
            <StyledTableCell className="header">Order ID</StyledTableCell>
            <StyledTableCell className="header">Customer</StyledTableCell>
            <StyledTableCell className="header">Payment Status</StyledTableCell>
            <StyledTableCell className="header">Total Amount</StyledTableCell>
            <StyledTableCell className="header">Reason</StyledTableCell>
            <StyledTableCell className="header" sx={{ position: "sticky", right: 0, zIndex: 5 }}>
              Actions
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ border: "none" }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography color="text.secondary">No orders found</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((order, index) => {
              const orderId = order.order_id ?? order.id;
              const isSelected = isProductSelected(order.id);
              const paymentDisplay = getPaymentStatusDisplay(order);
              const isExpanded = expandedRows.has(order.id);
              const cancelAction = getRequestAction(order, "cancel_request_action");
              const returnAction = getRequestAction(order, "return_request_action");
              const advancePayment = Array.isArray(order.advance_payment) ? order.advance_payment[0] : order.advance_payment;
              const requestReason = order.reason ?? order?.order?.reason ?? advancePayment?.order?.reason ?? "—";

              return (
                <React.Fragment key={order.id}>
                  <StyledTableRow hover selected={isSelected}>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {(currentPage - 1) * pageSize + index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(order.id);
                          }}
                          sx={{ p: 0.25, transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                        >
                          <KeyboardArrowRightIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" fontWeight={500}>{orderId ?? "—"}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatDateToIST(order.order_date || order.created_at || order.order_date_formatted)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2">
                        {order.full_name ?? order.user?.full_name ?? order.user_name ?? order.user?.username ?? "—"}
                      </Typography>
                      {(order.user_phone ?? order.user?.mobile_number ?? order.mobile_number) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {order.user_phone ?? order.user?.mobile_number ?? order.mobile_number}
                        </Typography>
                      )}
                      {order.farmer_name && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 0.5, color: theme.palette.info.main, fontWeight: 600, fontStyle: "italic" }}
                        >
                          Behalf of: {order.farmer_name}
                        </Typography>
                      )}
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <PaymentStatusChip
                        label={paymentDisplay.name || "—"}
                        status={(paymentDisplay.category || paymentDisplay.name || "pending")?.toLowerCase()}
                        size="small"
                      />
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(order.subtotal ? (order.subtotal + (order.gst_amount || 0)) : order.total_amount)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell onClick={() => toggleRow(order.id)}>
                      {(() => {
                        const reasonText = requestReason;
                        const reasonStr = String(reasonText);
                        const previewLength = 28;
                        const reasonPreview =
                          reasonStr.length > previewLength
                            ? `${reasonStr.slice(0, previewLength)}...`
                            : reasonStr;
                        return (
                          <Tooltip title={reasonStr} arrow placement="top">
                            <Typography variant="body2" sx={{ cursor: "help" }}>
                              {reasonPreview}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 1,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <Box display="flex" gap={1} sx={{ pr: 3 }}>
                        {showCancelRequestActions &&
                          onApproveCancelRequest &&
                          onRejectCancelRequest && (() => {
                            const isPending = cancelAction === null;
                            const isApproved = cancelAction === "approved";
                            const isRejected = cancelAction === "rejected";
                            const canAccept = shouldEnableButton ? shouldEnableButton('accepted') : true;
                            const canReject = shouldEnableButton ? shouldEnableButton('rejected') : true;
                            return (
                              <>
                                {(isPending || isApproved) && (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    disabled={isApproved || updatingCancelRequestOrderId === orderId || !canAccept}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isApproved && canAccept) onApproveCancelRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600, opacity: !canAccept ? 0.6 : 1 }}
                                  >
                                    {updatingCancelRequestOrderId === orderId ? <CircularProgress size={18} /> : "Accepted"}
                                  </Button>
                                )}
                                {(isPending || isRejected) && (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={isRejected || updatingCancelRequestOrderId === orderId || !canReject}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isRejected && canReject) onRejectCancelRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600, opacity: !canReject ? 0.6 : 1 }}
                                  >
                                    Rejected
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        {showReturnRequestActions &&
                          onApproveReturnRequest &&
                          onRejectReturnRequest && (() => {
                            const isPending = returnAction === null;
                            const isApproved = returnAction === "approved";
                            const isRejected = returnAction === "rejected";
                            const canAccept = shouldEnableButton ? shouldEnableButton('accepted') : true;
                            const canReject = shouldEnableButton ? shouldEnableButton('rejected') : true;
                            return (
                              <>
                                {(isPending || isApproved) && (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    disabled={isApproved || updatingReturnRequestOrderId === orderId || !canAccept}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isApproved && canAccept) onApproveReturnRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600, opacity: !canAccept ? 0.6 : 1 }}
                                  >
                                    {updatingReturnRequestOrderId === orderId ? <CircularProgress size={18} /> : "Accepted"}
                                  </Button>
                                )}
                                {(isPending || isRejected) && (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={isRejected || updatingReturnRequestOrderId === orderId || !canReject}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isRejected && canReject) onRejectReturnRequest(order);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 600, opacity: !canReject ? 0.6 : 1 }}
                                  >
                                    Rejected
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                  <OrderDetailsRow order={order} />
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default ReturnCancelRequestTable;